import { ArrowLeft, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { ROUTES } from '../../constants/routes';
import {
  useUpdateVariantThreshold,
  useVariantReorderInfo,
} from '../../hooks/inventory/useVariantReorder';

function formatVariantLabel(v: {
  product_name: string;
  size: string | null;
  color: string | null;
}): string {
  return [v.product_name, v.size, v.color].filter(Boolean).join(' · ');
}

export default function VariantDetail() {
  const { variantId } = useParams<{ variantId: string }>();
  const reorderQuery = useVariantReorderInfo(variantId);
  const updateThreshold = useUpdateVariantThreshold();

  const [overrideInput, setOverrideInput] = useState<string>('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (reorderQuery.data) {
      setOverrideInput(
        reorderQuery.data.override_threshold !== null
          ? String(reorderQuery.data.override_threshold)
          : '',
      );
    }
  }, [reorderQuery.data]);

  if (reorderQuery.isLoading) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <p className="text-white/60">Loading variant…</p>
      </div>
    );
  }

  if (reorderQuery.error || !reorderQuery.data) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <p className="text-red-400">Could not load this variant.</p>
        <Link to={ROUTES.INVENTORY_DASHBOARD} className="text-white/60 underline mt-4 inline-block">
          ← Back to inventory
        </Link>
      </div>
    );
  }

  const data = reorderQuery.data;
  const overrideActive = data.override_threshold !== null;
  const parsed = overrideInput.trim() === '' ? null : Number(overrideInput);
  const parsedInvalid =
    parsed !== null && (Number.isNaN(parsed) || parsed < 0 || !Number.isInteger(parsed));
  const dirty =
    !parsedInvalid &&
    (parsed === null ? overrideActive : parsed !== data.override_threshold);

  const handleSave = async () => {
    if (parsedInvalid || !variantId || !dirty) return;
    await updateThreshold.mutateAsync({ variantId, threshold: parsed });
    setSavedAt(Date.now());
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <Link
          to={ROUTES.INVENTORY_DASHBOARD}
          className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Inventory
        </Link>
        <h1 className="text-2xl font-bold text-[#EBFFEE] mt-2">{formatVariantLabel(data)}</h1>
        <p className="text-sm text-white/50 mt-1">SKU {data.sku}</p>
      </div>

      <Card className="bg-[#221F1F] border-[#504A4A]">
        <CardHeader>
          <CardTitle className="text-text-primary">Stock &amp; velocity</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <dt className="text-white/60">On hand (all locations)</dt>
            <dd className="font-mono text-text-primary">{data.on_hand}</dd>

            <dt className="text-white/60">Units sold (30d)</dt>
            <dd className="font-mono text-text-primary">{data.units_30d}</dd>

            <dt className="text-white/60">Units / day</dt>
            <dd className="font-mono text-text-primary">{data.units_per_day.toFixed(2)}</dd>

            <dt className="text-white/60">Lead time (company)</dt>
            <dd className="font-mono text-text-primary">{data.lead_time_days} days</dd>
          </dl>
        </CardContent>
      </Card>

      <Card className="bg-[#221F1F] border-[#504A4A]">
        <CardHeader>
          <CardTitle className="text-text-primary">Reorder recommendation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <div className="text-white/60 text-sm">System-recommended reorder qty</div>
            <div className="font-mono text-2xl text-text-primary mt-1">
              {data.system_recommended_qty > 0 ? `+${data.system_recommended_qty}` : '—'}
            </div>
            <div className="text-xs text-white/50 mt-1">
              Targets {data.lead_time_days + 7} days of cover (lead time + 7d safety) at current velocity.
            </div>
          </div>

          <div className="border-t border-[#363232] pt-5">
            <label htmlFor="threshold-override" className="text-white/60 text-sm block mb-1">
              Alert threshold override
            </label>
            <p className="text-xs text-white/50 mb-3">
              On-hand level that triggers a low-stock alert. Leave blank to use the system default
              ({data.system_threshold} units, derived from velocity × lead time). Override wins when set.
            </p>
            <div className="flex items-end gap-3">
              <div className="flex-1 max-w-[180px]">
                <Input
                  id="threshold-override"
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  placeholder={`System: ${data.system_threshold}`}
                  value={overrideInput}
                  onChange={(e) => setOverrideInput(e.target.value)}
                  aria-invalid={parsedInvalid}
                />
              </div>
              <Button
                onClick={handleSave}
                disabled={!dirty || parsedInvalid || updateThreshold.isPending}
                className="gap-2"
              >
                <Save className="w-4 h-4" />
                {updateThreshold.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
            {parsedInvalid && (
              <div className="text-red-400 text-xs mt-2">
                Enter a non-negative whole number, or leave blank to clear.
              </div>
            )}
            {updateThreshold.error && (
              <div className="text-red-400 text-xs mt-2">
                {(updateThreshold.error as Error).message ?? 'Save failed.'}
              </div>
            )}
            {savedAt && !dirty && !updateThreshold.isPending && !updateThreshold.error && (
              <div className="text-emerald-400 text-xs mt-2">Saved.</div>
            )}

            <div className="mt-4 text-sm">
              <span className="text-white/60">Effective threshold: </span>
              <span className="font-mono text-text-primary">{data.effective_threshold}</span>
              <span className="text-white/50 ml-2">
                ({overrideActive ? 'owner override' : 'system default'})
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
